import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Lock, Plus, Share2, Trash2, X } from 'lucide-react'
import type {
  CfoAudienceOptions,
  CfoEntitlementDimension,
  CfoEntitlementRule,
  CfoReportShare,
  ServerApiClient,
} from '../../data/ServerApiClient'

// Minimal admin surface for the CFO Core "sharing-security" feature. It manages
// two workbook-scoped concepts that are both enforced server-side (this panel
// only edits the rules):
//   1. Segment/account ENTITLEMENTS - restrict which accounts/segments a given
//      user or group may see in this workbook's CFO statements.
//   2. Per-audience REPORT SHARES - grant a restricted, view-only audience access
//      to this workbook's CFO presentation (recipients still see only the
//      segments their own entitlements allow).

const DIMENSION_LABELS: Record<CfoEntitlementDimension, string> = {
  account_group: 'Account group (classification)',
  account_type: 'Account type',
  account_sub_type: 'Account sub type',
  account_id: 'Account id',
  class: 'Class',
  location: 'Location / department',
}

const DIMENSION_OPTIONS = Object.keys(DIMENSION_LABELS) as CfoEntitlementDimension[]

type DraftRule = {
  subject_kind: 'user' | 'group'
  subject_id: string
  effect: 'allow' | 'deny'
  dimension: CfoEntitlementDimension
  valuesText: string
  description: string
}

type DraftShare = {
  subject_kind: 'user' | 'group'
  subject_id: string
  audience_label: string
}

type Props = {
  appApi: ServerApiClient
  workbookId: string
  onClose: () => void
}

export function CfoSharingPanel({ appApi, workbookId, onClose }: Props) {
  const [options, setOptions] = useState<CfoAudienceOptions | null>(null)
  const [rules, setRules] = useState<DraftRule[]>([])
  const [shares, setShares] = useState<DraftShare[]>([])
  const [loading, setLoading] = useState(true)
  const [savingRules, setSavingRules] = useState(false)
  const [savingShares, setSavingShares] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<string | null>(null)

  const toDraftRule = useCallback(
    (rule: CfoEntitlementRule): DraftRule => ({
      subject_kind: rule.subject_kind,
      subject_id: rule.subject_id,
      effect: rule.effect,
      dimension: rule.dimension,
      valuesText: (rule.dimension_values || []).join(', '),
      description: rule.description || '',
    }),
    [],
  )

  const toDraftShare = useCallback(
    (share: CfoReportShare): DraftShare => ({
      subject_kind: share.subject_kind,
      subject_id: share.subject_id,
      audience_label: share.audience_label || '',
    }),
    [],
  )

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    Promise.all([
      appApi.listCfoAudienceOptions(workbookId),
      appApi.listCfoEntitlements(workbookId),
      appApi.listCfoReportShares(workbookId),
    ])
      .then(([opts, ruleRows, shareRows]) => {
        if (cancelled) return
        setOptions(opts)
        setRules(ruleRows.map(toDraftRule))
        setShares(shareRows.map(toDraftShare))
      })
      .catch((e: any) => {
        if (cancelled) return
        setError(String(e?.message || e || 'Failed to load sharing settings'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [appApi, workbookId, toDraftRule, toDraftShare])

  const defaultSubjectId = useMemo(() => {
    if (options?.groups?.length) return options.groups[0].group_id
    if (options?.users?.length) return options.users[0].user_id
    return ''
  }, [options])

  const subjectName = useCallback(
    (kind: 'user' | 'group', id: string): string => {
      if (kind === 'group') {
        return options?.groups?.find((g) => g.group_id === id)?.name || id
      }
      return options?.users?.find((u) => u.user_id === id)?.email || id
    },
    [options],
  )

  const subjectChoices = useCallback(
    (kind: 'user' | 'group') =>
      kind === 'group'
        ? (options?.groups || []).map((g) => ({ id: g.group_id, label: g.name }))
        : (options?.users || []).map((u) => ({ id: u.user_id, label: u.email })),
    [options],
  )

  const addRule = useCallback(() => {
    setRules((prev) => [
      ...prev,
      {
        subject_kind: 'group',
        subject_id: defaultSubjectId,
        effect: 'allow',
        dimension: 'account_group',
        valuesText: '',
        description: '',
      },
    ])
  }, [defaultSubjectId])

  const addShare = useCallback(() => {
    setShares((prev) => [
      ...prev,
      { subject_kind: 'group', subject_id: defaultSubjectId, audience_label: '' },
    ])
  }, [defaultSubjectId])

  const updateRule = useCallback((index: number, patch: Partial<DraftRule>) => {
    setRules((prev) => prev.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)))
  }, [])

  const updateShare = useCallback((index: number, patch: Partial<DraftShare>) => {
    setShares((prev) => prev.map((share, i) => (i === index ? { ...share, ...patch } : share)))
  }, [])

  const saveRules = useCallback(async () => {
    setSavingRules(true)
    setError(null)
    setStatus(null)
    try {
      const payload = rules
        .map((rule) => ({
          subject_kind: rule.subject_kind,
          subject_id: rule.subject_id,
          effect: rule.effect,
          dimension: rule.dimension,
          dimension_values: rule.valuesText
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v.length > 0),
          description: rule.description.trim() || null,
        }))
        .filter((rule) => rule.subject_id && rule.dimension_values.length > 0)
      const saved = await appApi.putCfoEntitlements(workbookId, payload)
      setRules(saved.map(toDraftRule))
      setStatus('Entitlement rules saved. Statements now honor these limits for affected viewers.')
    } catch (e: any) {
      setError(String(e?.message || e || 'Failed to save entitlement rules'))
    } finally {
      setSavingRules(false)
    }
  }, [appApi, rules, toDraftRule, workbookId])

  const saveShares = useCallback(async () => {
    setSavingShares(true)
    setError(null)
    setStatus(null)
    try {
      const payload = shares
        .filter((share) => share.subject_id)
        .map((share) => ({
          subject_kind: share.subject_kind,
          subject_id: share.subject_id,
          audience_label: share.audience_label.trim() || null,
        }))
      const saved = await appApi.putCfoReportShares(workbookId, payload)
      setShares(saved.map(toDraftShare))
      setStatus('Report sharing updated. Recipients get a view-only report scoped to their entitlements.')
    } catch (e: any) {
      setError(String(e?.message || e || 'Failed to save report shares'))
    } finally {
      setSavingShares(false)
    }
  }, [appApi, shares, toDraftShare, workbookId])

  return (
    <aside className="fg-cfo-sharing-panel" data-testid="cfo-sharing-panel" style={PANEL_STYLE}>
      <div style={HEADER_STYLE}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Lock size={16} />
          <strong>Sharing &amp; access</strong>
        </div>
        <button type="button" aria-label="Close sharing panel" onClick={onClose} style={ICON_BTN_STYLE}>
          <X size={16} />
        </button>
      </div>

      {loading ? (
        <p style={MUTED_STYLE}>Loading sharing settings...</p>
      ) : (
        <>
          {error ? (
            <div style={ERROR_STYLE} data-testid="cfo-sharing-error">
              {error}
            </div>
          ) : null}
          {status ? (
            <div style={STATUS_STYLE} data-testid="cfo-sharing-status">
              {status}
            </div>
          ) : null}

          <section style={SECTION_STYLE}>
            <div style={SECTION_HEAD_STYLE}>
              <div>
                <strong>Segment &amp; account entitlements</strong>
                <p style={MUTED_STYLE}>
                  Restrict which accounts or segments a user/group can see in this workbook&apos;s
                  statements. No rule = full access. Allow-rules whitelist values for a dimension;
                  deny-rules always win. Enforced server-side at the render boundary.
                </p>
              </div>
              <button type="button" onClick={addRule} style={GHOST_BTN_STYLE} data-testid="cfo-entitlement-add">
                <Plus size={14} /> Add rule
              </button>
            </div>
            {rules.length === 0 ? (
              <p style={MUTED_STYLE}>No restrictions. Everyone with workbook access sees all accounts.</p>
            ) : (
              <div style={{ display: 'grid', gap: 10 }}>
                {rules.map((rule, index) => (
                  <div key={`rule-${index}`} style={ROW_CARD_STYLE} data-testid="cfo-entitlement-row">
                    <div style={ROW_GRID_STYLE}>
                      <label style={FIELD_STYLE}>
                        Subject
                        <select
                          value={rule.subject_kind}
                          onChange={(e) =>
                            updateRule(index, {
                              subject_kind: e.target.value as 'user' | 'group',
                              subject_id: '',
                            })
                          }
                        >
                          <option value="group">Group</option>
                          <option value="user">User</option>
                        </select>
                      </label>
                      <label style={FIELD_STYLE}>
                        {rule.subject_kind === 'group' ? 'Group' : 'User'}
                        <select
                          value={rule.subject_id}
                          onChange={(e) => updateRule(index, { subject_id: e.target.value })}
                        >
                          <option value="">Select...</option>
                          {subjectChoices(rule.subject_kind).map((choice) => (
                            <option key={choice.id} value={choice.id}>
                              {choice.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label style={FIELD_STYLE}>
                        Effect
                        <select
                          value={rule.effect}
                          onChange={(e) => updateRule(index, { effect: e.target.value as 'allow' | 'deny' })}
                        >
                          <option value="allow">Allow only</option>
                          <option value="deny">Deny</option>
                        </select>
                      </label>
                      <label style={FIELD_STYLE}>
                        Dimension
                        <select
                          value={rule.dimension}
                          onChange={(e) =>
                            updateRule(index, { dimension: e.target.value as CfoEntitlementDimension })
                          }
                        >
                          {DIMENSION_OPTIONS.map((dim) => (
                            <option key={dim} value={dim}>
                              {DIMENSION_LABELS[dim]}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <label style={FIELD_STYLE}>
                      Values (comma separated)
                      <input
                        type="text"
                        value={rule.valuesText}
                        placeholder={
                          rule.dimension === 'account_group'
                            ? 'e.g. Revenue, Expense'
                            : 'e.g. 5, 12, 40'
                        }
                        onChange={(e) => updateRule(index, { valuesText: e.target.value })}
                      />
                    </label>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        onClick={() => setRules((prev) => prev.filter((_, i) => i !== index))}
                        style={DANGER_BTN_STYLE}
                        aria-label="Remove rule"
                      >
                        <Trash2 size={14} /> Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button
                type="button"
                onClick={saveRules}
                disabled={savingRules}
                style={PRIMARY_BTN_STYLE}
                data-testid="cfo-entitlement-save"
              >
                {savingRules ? 'Saving...' : 'Save entitlements'}
              </button>
            </div>
          </section>

          <section style={SECTION_STYLE}>
            <div style={SECTION_HEAD_STYLE}>
              <div>
                <strong>Share this report (view-only)</strong>
                <p style={MUTED_STYLE}>
                  Give specific users or groups read-only access to this CFO presentation. Each
                  recipient still only sees the segments their entitlements allow.
                </p>
              </div>
              <button type="button" onClick={addShare} style={GHOST_BTN_STYLE} data-testid="cfo-share-add">
                <Share2 size={14} /> Add audience
              </button>
            </div>
            {shares.length === 0 ? (
              <p style={MUTED_STYLE}>Not shared with anyone outside the workbook&apos;s project members.</p>
            ) : (
              <div style={{ display: 'grid', gap: 10 }}>
                {shares.map((share, index) => (
                  <div key={`share-${index}`} style={ROW_CARD_STYLE} data-testid="cfo-share-row">
                    <div style={ROW_GRID_STYLE}>
                      <label style={FIELD_STYLE}>
                        Subject
                        <select
                          value={share.subject_kind}
                          onChange={(e) =>
                            updateShare(index, {
                              subject_kind: e.target.value as 'user' | 'group',
                              subject_id: '',
                            })
                          }
                        >
                          <option value="group">Group</option>
                          <option value="user">User</option>
                        </select>
                      </label>
                      <label style={FIELD_STYLE}>
                        {share.subject_kind === 'group' ? 'Group' : 'User'}
                        <select
                          value={share.subject_id}
                          onChange={(e) => updateShare(index, { subject_id: e.target.value })}
                        >
                          <option value="">Select...</option>
                          {subjectChoices(share.subject_kind).map((choice) => (
                            <option key={choice.id} value={choice.id}>
                              {choice.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label style={{ ...FIELD_STYLE, gridColumn: 'span 2' }}>
                        Audience label (optional)
                        <input
                          type="text"
                          value={share.audience_label}
                          placeholder="e.g. East region managers"
                          onChange={(e) => updateShare(index, { audience_label: e.target.value })}
                        />
                      </label>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={MUTED_STYLE}>
                        {share.subject_id ? `Shared with ${subjectName(share.subject_kind, share.subject_id)}` : ''}
                      </span>
                      <button
                        type="button"
                        onClick={() => setShares((prev) => prev.filter((_, i) => i !== index))}
                        style={DANGER_BTN_STYLE}
                        aria-label="Remove share"
                      >
                        <Trash2 size={14} /> Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button
                type="button"
                onClick={saveShares}
                disabled={savingShares}
                style={PRIMARY_BTN_STYLE}
                data-testid="cfo-share-save"
              >
                {savingShares ? 'Saving...' : 'Save sharing'}
              </button>
            </div>
          </section>
        </>
      )}
    </aside>
  )
}

const PANEL_STYLE: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  width: 'min(520px, 92vw)',
  background: '#ffffff',
  borderLeft: '1px solid rgba(15, 23, 42, 0.12)',
  boxShadow: '-24px 0 70px rgba(15, 23, 42, 0.18)',
  padding: 20,
  overflowY: 'auto',
  zIndex: 60,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
}

const HEADER_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}

const SECTION_STYLE: React.CSSProperties = {
  border: '1px solid rgba(15, 23, 42, 0.1)',
  borderRadius: 10,
  padding: 14,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
}

const SECTION_HEAD_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
}

const ROW_CARD_STYLE: React.CSSProperties = {
  border: '1px solid rgba(15, 23, 42, 0.08)',
  borderRadius: 8,
  padding: 10,
  background: 'rgba(15, 23, 42, 0.02)',
  display: 'grid',
  gap: 8,
}

const ROW_GRID_STYLE: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 8,
}

const FIELD_STYLE: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 12,
  fontWeight: 600,
  color: 'rgba(15, 23, 42, 0.7)',
}

const MUTED_STYLE: React.CSSProperties = {
  fontSize: 12,
  color: 'rgba(15, 23, 42, 0.55)',
  margin: '4px 0 0',
  fontWeight: 400,
  lineHeight: 1.4,
}

const PRIMARY_BTN_STYLE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: '#0f172a',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '8px 14px',
  fontWeight: 600,
  cursor: 'pointer',
}

const GHOST_BTN_STYLE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'transparent',
  border: '1px solid rgba(15, 23, 42, 0.18)',
  borderRadius: 8,
  padding: '6px 10px',
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
}

const DANGER_BTN_STYLE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'transparent',
  border: '1px solid rgba(220, 38, 38, 0.3)',
  color: '#b91c1c',
  borderRadius: 8,
  padding: '6px 10px',
  fontWeight: 600,
  cursor: 'pointer',
}

const ICON_BTN_STYLE: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: 'rgba(15, 23, 42, 0.6)',
}

const ERROR_STYLE: React.CSSProperties = {
  background: 'rgba(220, 38, 38, 0.08)',
  border: '1px solid rgba(220, 38, 38, 0.25)',
  color: '#b91c1c',
  borderRadius: 8,
  padding: '8px 12px',
  fontSize: 13,
}

const STATUS_STYLE: React.CSSProperties = {
  background: 'rgba(16, 185, 129, 0.08)',
  border: '1px solid rgba(16, 185, 129, 0.25)',
  color: '#047857',
  borderRadius: 8,
  padding: '8px 12px',
  fontSize: 13,
}

export default CfoSharingPanel
